/**
 * ObservationsManager — API du rattachement plan↔observation.
 *
 * Le champ `observationId` du DR_META porte le `localId` (UUID) de la
 * FieldObservation rattachée — le plan est la source de vérité de l'appartenance.
 * Délègue aux fonctions pures de `meta/observation.ts`.
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { CommandHistory } from "../commands/CommandHistory";
import {
  setEntitiesObservation,
  listObservationEntities,
  clearObservationEntities,
  type ObservationEntityRef,
} from "../meta/observation";
import {
  scanAnalysisCandidates,
  mapAnalysisEntityHandles,
  type AnalysisScanOptions,
  type AnalysisScanResult,
} from "../meta/analysis-scan";
import type { PlanUnit } from "@/utils/plan-analysis/types";

/** `$INSUNITS` (OdDb::UnitsValue) → unité du plan ; les autres valeurs = inconnue. */
const INSUNITS_TO_PLAN_UNIT: Record<number, PlanUnit> = {
  4: "mm",
  5: "cm",
  6: "m",
};

export class ObservationsManager {
  constructor(
    private viewer: DRDrawingViewer,
    private history: CommandHistory
  ) {}

  /**
   * Rattache (ou détache avec `null`) les entités `handles` à l'observation.
   * @returns le nombre d'entités effectivement stampées.
   */
  setEntities(handles: string[], observationId: string | null): number {
    const count = setEntitiesObservation(this.viewer, handles, observationId);
    if (count > 0) this.markDirty();
    return count;
  }

  /** Entités rattachées, groupées par `observationId` (une traversée). */
  listEntities(): Map<string, ObservationEntityRef[]> {
    return listObservationEntities(this.viewer);
  }

  /**
   * Unité du plan déclarée dans le DWG (`$INSUNITS`), null si absente ou
   * exotique — l'inspecteur confirme ou choisit dans l'app.
   */
  readPlanUnit(): PlanUnit | null {
    try {
      const value = this.viewer.database?.getINSUNITS();
      return value === undefined ? null : (INSUNITS_TO_PLAN_UNIT[value] ?? null);
    } catch {
      return null;
    }
  }

  /**
   * Candidates à l'analyse du plan (non rattachées, hors fond de plan) +
   * emprise du plan entier. Lecture seule, une traversée.
   */
  scanAnalysisCandidates(options?: AnalysisScanOptions): AnalysisScanResult {
    return scanAnalysisCandidates(this.viewer, options);
  }

  /** Index `entityUid` → handle courant (réhydratation d'une analyse persistée). */
  mapAnalysisEntityHandles(): Map<string, string> {
    return mapAnalysisEntityHandles(this.viewer);
  }

  /**
   * Détache toutes les entités rattachées à `observationId` (suppression de
   * l'observation).
   * @returns le nombre d'entités détachées.
   */
  clear(observationId: string): number {
    const count = clearObservationEntities(this.viewer, observationId);
    if (count > 0) this.markDirty();
    return count;
  }

  /**
   * Le rattachement vit dans le XData du DWG : sans ce marquage, le dessin
   * n'est pas considéré comme modifié, aucune sauvegarde n'est déclenchée et le
   * lien disparaît au prochain rechargement du dessin (changement de version,
   * réouverture…). Pas de commande d'historique : le rattachement n'est pas une
   * édition de tracé, il n'a pas à se défaire au Ctrl+Z.
   */
  private markDirty(): void {
    this.history.markDirty();
  }
}

export type { ObservationEntityRef, AnalysisScanResult };
